import { RefObject, useEffect, useRef } from 'react';
import { cx } from '@emotion/css';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import FormControl from '@mui/material/FormControl';
import Popover from '@mui/material/Popover';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import { BodyLargeEmphasized, BodyMediumMidEmphasis } from '@/design-system';
import { AccessibleDivButton, Drawer } from '@/components';
import colors from '@/theme/colors';
import { useIsMobile } from '@/hooks';
import styles from './SortMenu.styles';

type Option = {
  value: string;
  label: string;
  description?: string;
};

interface Props {
  open: boolean;
  onClose: () => void;
  onChange: (value: string) => void;
  selectedOption: string;
  anchorRef: RefObject<HTMLDivElement>;
  options: Option[];
}

export const SortMenu: React.FC<Props> = ({
  open,
  onClose,
  onChange,
  selectedOption,
  anchorRef,
  options,
}) => {
  const isMobile = useIsMobile();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const sortByLabelId = 'sortByLabel';

  useEffect(() => {
    if (open && !isMobile && titleRef.current) {
      titleRef.current.focus();
    }
  }, [open, isMobile]);

  const handleOptionClick = (value: string) => {
    onChange(value);
  };

  const SortPopover = () => (
    <Popover
      open={open}
      anchorEl={anchorRef.current}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        className: styles.popper,
        style: { borderRadius: '12px' },
        role: 'dialog',
        'aria-modal': 'true',
        'aria-labelledby': sortByLabelId,
      }}
    >
      <div>
        <div className={styles.popoverTitle}>
          <AccessibleDivButton className={styles.closeButton} onClick={onClose} aria-label="Close">
            <CloseOutlinedIcon sx={{ fontSize: '20px' }} />
          </AccessibleDivButton>
          <BodyLargeEmphasized
            as="h1"
            id={sortByLabelId}
            tabIndex={-1}
            ref={titleRef}
            className={styles.dialogTitle}
          >
            Sort by
          </BodyLargeEmphasized>
        </div>
        <RadioGroup aria-labelledby={sortByLabelId} name="radio-buttons-group" value={selectedOption}>
          <MenuList autoFocusItem={false} role="presentation">
            {options.map(({ value, label, description }) => (
              <MenuItem
                key={value}
                onClick={() => handleOptionClick(value)}
                className={styles.option}
                selected={selectedOption === value}
                disableRipple
              >
                <FormControlLabel
                  value={value}
                  control={
                    <Radio
                      sx={{
                        '&.Mui-checked': {
                          color: `${colors.jungle}`,
                        },
                      }}
                      autoFocus={selectedOption === value}
                    />
                  }
                  label={
                    <div className={styles.column}>
                      <BodyMediumMidEmphasis>{label}</BodyMediumMidEmphasis>
                      {description && (
                        <BodyMediumMidEmphasis color={colors.slate}>
                          {description}
                        </BodyMediumMidEmphasis>
                      )}
                    </div>
                  }
                  sx={{ margin: 0, pointerEvents: 'none' }}
                />
              </MenuItem>
            ))}
          </MenuList>
        </RadioGroup>
      </div>
    </Popover>
  );

  const SortDrawer = () => (
    <Drawer title="Sort by" titleId={sortByLabelId} isOpen={open} onToggle={onClose}>
      <div className={styles.content}>
        <FormControl>
          <RadioGroup
            aria-labelledby={sortByLabelId}
            name="radio-buttons-group"
            value={selectedOption}
          >
            {options.map(({ value, label, description }) => (
              <FormControlLabel
                key={value}
                value={value}
                onClick={() => handleOptionClick(value)}
                control={
                  <Radio
                    sx={{
                      '&.Mui-checked': {
                        color: `${colors.jungle}`,
                      },
                    }}
                    aria-describedby={description ? `desc-${value}` : undefined}
                  />
                }
                label={
                  <div className={styles.column}>
                    <BodyMediumMidEmphasis>{label}</BodyMediumMidEmphasis>
                    {description && (
                      <BodyMediumMidEmphasis id={`desc-${value}`} color={colors.slate}>
                        {description}
                      </BodyMediumMidEmphasis>
                    )}
                  </div>
                }
                className={cx(
                  styles.formLabel,
                  selectedOption === value && styles.formLabelSelected,
                )}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </div>
    </Drawer>
  );

  return isMobile ? <SortDrawer /> : <SortPopover />;
};
